/*
|------------------------------------------------------------------------------
| Role gates — mirrors of the API's own middleware
|------------------------------------------------------------------------------
| Every constant here copies a `role:` gate from the Laravel route files, and
| the comment names the file it came from. When a gate changes on the server,
| change it here in the same commit.
|
| Why this file exists: nav items and route guards were declared in two places
| with no shared definition, and an item with no `roles` key defaults to
| visible. Roles were therefore shown whole subsystems the API rejects — the
| page would render and every request inside it would come back "User does not
| have the right roles." Hiding a surface a role cannot use is not cosmetic:
| an operator cannot tell a permissions error from an outage.
|
| A page is gated on the INTERSECTION of the gates it calls. A screen that
| loads from two route groups needs both, so the narrower list wins.
*/

// ── rerr.php ────────────────────────────────────────────────────────────────
// Agency-scoped work. Excludes hub_admin and inspector_general deliberately:
// the controller requires an agency membership and aborts without one, so
// neither can use these screens however senior they are.
export const RERR_AGENCY = ['agency_officer', 'agency_supervisor', 'agency_admin'];

// hub.php and the read half of rerr.php's hub block. Oversight and the admin
// tier read these; none of them can write. The agency registry mutations stay
// hub_admin, which is why the write list below is separate — the admin tier
// administers the platform, it does not run the subsystems.
export const HUB_OVERSIGHT = ['hub_admin', 'inspector_general', 'platform_admin', 'super_admin'];

// Agency registry create/update — hub_admin alone.
export const HUB_ADMIN_ONLY = ['hub_admin'];

// ── cad.php ─────────────────────────────────────────────────────────────────
export const CAD_ALL = ['agency_officer', 'agency_supervisor', 'agency_admin', 'hub_admin'];

// ── vision.php ──────────────────────────────────────────────────────────────
export const VISION_AGENCY = ['agency_officer', 'agency_supervisor', 'agency_admin', 'hub_admin'];

// Same middleware as VISION_AGENCY, but the fleet controller additionally
// calls requireAgencyId() and aborts without an agency membership — so
// hub_admin passes the route gate and is then refused by the handler. The
// middleware list is not the whole gate wherever a controller scopes by
// agency, and this is the list that actually holds.
export const VISION_AGENCY_SCOPED = ['agency_officer', 'agency_supervisor', 'agency_admin'];
// vision.php admin block and cbim.php admin block share this list.
export const VISION_ADMIN = ['hub_admin', 'inspector_general', 'ifps_supervisor'];

// ── judicial.php ────────────────────────────────────────────────────────────
export const JUDICIAL_OFFICER  = ['agency_officer', 'agency_supervisor', 'agency_admin', 'hub_admin'];
export const JUDICIAL_REVIEWER = ['judge', 'magistrate', 'prosecutor', 'hub_admin', 'inspector_general'];

// ── firl.php ────────────────────────────────────────────────────────────────
export const FIRL_CASEWORK    = ['program_officer', 'firl_admin', 'hub_admin'];
export const FIRL_MONITORING  = ['firl_admin', 'hub_admin', 'inspector_general'];
export const FIRL_ENFORCEMENT = ['firl_admin', 'hub_admin'];

// ── veris.php ───────────────────────────────────────────────────────────────
// Reads. Writes keep the narrower list below: reading a registration is
// oversight, changing one is disenfranchisement.
export const VERIS_CASEWORK      = ['election_official', 'veris_admin', 'hub_admin', 'inspector_general'];
export const VERIS_CASEWORK_WRITE = ['election_official', 'veris_admin', 'hub_admin'];
export const VERIS_DUAL_CONTROL = ['veris_adjudication_officer', 'veris_admin', 'hub_admin', 'inspector_general'];
export const VERIS_PROPAGATION  = ['veris_admin', 'hub_admin', 'inspector_general'];

// ── ifps.php ────────────────────────────────────────────────────────────────
export const IFPS_OPERATOR = ['ifps_supervisor', 'hub_admin'];
export const IFPS_PARTNER  = ['institutional_partner'];

// ── pmics.php / csdr.php / cdms.php ─────────────────────────────────────────
export const REGISTRY = ['ifps_supervisor', 'hub_admin'];

// ── osint.php ───────────────────────────────────────────────────────────────
// Signals and red-flag reads are the narrower of the two lists in that file.
export const OSINT_READ = ['hub_admin', 'ifps_supervisor', 'inspector_general'];

// ── national_security.php ───────────────────────────────────────────────────
export const NSA_READ = ['hub_admin', 'inspector_general', 'agency_admin', 'agency_supervisor', 'ifps_supervisor'];

// ── platform.php ────────────────────────────────────────────────────────────
export const PLATFORM_ADMIN = ['platform_admin'];

// ── citizen surfaces ────────────────────────────────────────────────────────
// No role gate on the API; authentication and device attestation only.
export const CITIZEN = ['citizen'];

/** Union of several gates — who may enter a subsystem at all. */
export const anyOf = (...lists) => [...new Set(lists.flat())];

// Who can load at least one page in each shell.
export const AGENCY_ENTRY   = anyOf(RERR_AGENCY, CAD_ALL, VISION_AGENCY, JUDICIAL_OFFICER);
export const FIRL_ENTRY     = anyOf(FIRL_CASEWORK, FIRL_MONITORING, FIRL_ENFORCEMENT);
export const VERIS_ENTRY    = anyOf(VERIS_CASEWORK, VERIS_DUAL_CONTROL, VERIS_PROPAGATION);
export const IFPS_ENTRY     = anyOf(IFPS_OPERATOR, IFPS_PARTNER);
export const HUB_ENTRY      = anyOf(HUB_OVERSIGHT, VISION_ADMIN, REGISTRY, OSINT_READ, NSA_READ, JUDICIAL_REVIEWER, PLATFORM_ADMIN);
export const JUDICIAL_ENTRY = JUDICIAL_REVIEWER;
